// Jira import wizard: Connect → pick project → preview → import (with live
// progress). Creates a brand-new project from the chosen Jira project.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Plugs, CircleNotch, MagnifyingGlass, CheckCircle, WarningCircle, ArrowLeft,
  ArrowRight, Kanban, Tag, ListChecks, DownloadSimple, Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function JiraImportDialog({ open, onClose, onOpenProject }) {
  const { API, getOrgHeaders } = useAuth();
  const cfg = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  const [step, setStep] = useState('connect');
  const [conn, setConn] = useState(null);
  const [form, setForm] = useState({ site_url: '', email: '', api_token: '' });
  const [connecting, setConnecting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const loadProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const { data } = await axios.get(`${API}/jira/projects`, cfg());
      setProjects(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load Jira projects'); }
    finally { setLoadingProjects(false); }
  }, [API, cfg]);

  // On open: check existing connection.
  useEffect(() => {
    if (!open) return;
    setStep('connect'); setSelected(null); setPreview(null); setJob(null); setSearch('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/jira/connection`, cfg());
        if (data.connected) { setConn(data); setStep('projects'); loadProjects(); }
        else { setConn(null); }
      } catch { setConn(null); }
    })();
  }, [open, API, cfg, loadProjects]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const doConnect = async () => {
    if (!form.site_url || !form.email || !form.api_token) { toast.error('Fill site URL, email and API token'); return; }
    setConnecting(true);
    try {
      const { data } = await axios.post(`${API}/jira/connection`, form, cfg());
      setConn(data);
      toast.success(`Connected as ${data.verified_as || data.email}`);
      setStep('projects'); loadProjects();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Connection failed'); }
    finally { setConnecting(false); }
  };

  const disconnect = async () => {
    try { await axios.delete(`${API}/jira/connection`, cfg()); } catch { /* noop */ }
    setConn(null); setProjects([]); setForm({ site_url: conn?.site_url || '', email: conn?.email || '', api_token: '' }); setStep('connect');
  };

  const pickProject = async (p) => {
    setSelected(p); setLoadingPreview(true); setStep('preview'); setPreview(null);
    try {
      const { data } = await axios.post(`${API}/jira/preview`, { project_key: p.key }, cfg());
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); setStep('projects'); }
    finally { setLoadingPreview(false); }
  };

  const startImport = async () => {
    try {
      const { data } = await axios.post(`${API}/jira/import`, { project_key: selected.key, project_name: selected.name }, cfg());
      setStep('progress');
      setJob({ status: 'queued' });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await axios.get(`${API}/jira/import/${data.job_id}`, cfg());
          setJob(j);
          if (j.status === 'done' || j.status === 'error') { clearInterval(pollRef.current); pollRef.current = null; }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start import'); }
  };

  const close = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } onClose(); };

  const filtered = projects.filter(p => `${p.name} ${p.key}`.toLowerCase().includes(search.toLowerCase()));
  const total = job?.total || 0;
  const fetched = job?.fetched || 0;
  const processed = job?.processed || 0;
  const pct = job?.status === 'done' ? 100 : (total ? Math.round((processed / total) * 100) : (fetched ? 35 : 10));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="!w-[720px] !max-w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="jira-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-6 h-6 rounded bg-[#0052CC] flex items-center justify-center"><Plugs size={14} weight="fill" className="text-white" /></span>
            Import from Jira
          </DialogTitle>
        </DialogHeader>

        {/* STEP: Connect */}
        {step === 'connect' && (
          <div className="space-y-3 py-1" data-testid="jira-step-connect">
            <p className="text-sm text-zinc-500">Connect your Jira Cloud site with your Atlassian email and a personal API token. We read your issues — nothing is written back to Jira.</p>
            <div>
              <Label className="text-xs text-zinc-600">Jira site URL</Label>
              <Input placeholder="https://yourcompany.atlassian.net" value={form.site_url} onChange={(e) => setForm(f => ({ ...f, site_url: e.target.value }))} data-testid="jira-site-url" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Atlassian account email</Label>
              <Input type="email" placeholder="you@company.com" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="jira-email" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">API token</Label>
              <Input type="password" placeholder="Paste your API token" value={form.api_token} onChange={(e) => setForm(f => ({ ...f, api_token: e.target.value }))} data-testid="jira-token" />
              <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline mt-1 inline-block">Create an API token →</a>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} data-testid="jira-connect-cancel">Cancel</Button>
              <Button onClick={doConnect} disabled={connecting} data-testid="jira-connect-btn">
                {connecting ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <Plugs size={16} className="mr-1.5" />}
                {connecting ? 'Connecting…' : 'Connect'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* STEP: Choose project */}
        {step === 'projects' && (
          <div className="space-y-3 py-1" data-testid="jira-step-projects">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-500">Connected as <span className="font-medium text-zinc-700">{conn?.display_name}</span> · {conn?.site_url?.replace('https://', '')}</span>
              <button onClick={disconnect} className="text-red-600 hover:underline flex items-center gap-1" data-testid="jira-disconnect"><Trash size={12} /> Disconnect</button>
            </div>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input className="pl-9" placeholder="Search projects…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="jira-project-search" />
            </div>
            <div className="max-h-[340px] overflow-y-auto space-y-1.5">
              {loadingProjects ? (
                <div className="py-10 text-center text-zinc-400"><CircleNotch size={22} className="animate-spin mx-auto" /></div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-400">No projects found.</p>
              ) : filtered.map(p => (
                <button key={p.id} onClick={() => pickProject(p)} data-testid={`jira-project-${p.key}`}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-zinc-200 hover:border-blue-400 hover:bg-blue-50/40 text-left transition-colors">
                  {p.avatar ? <img src={p.avatar} alt="" className="w-7 h-7 rounded" /> : <span className="w-7 h-7 rounded bg-zinc-200 flex items-center justify-center text-[10px] font-bold text-zinc-600">{p.key?.slice(0, 2)}</span>}
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium text-zinc-900 truncate">{p.name}</p><p className="text-[11px] text-zinc-400">{p.key}</p></div>
                  <ArrowRight size={15} className="text-zinc-300" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP: Preview */}
        {step === 'preview' && (
          <div className="space-y-4 py-1 min-w-0 w-full" data-testid="jira-step-preview">
            <button onClick={() => setStep('projects')} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1"><ArrowLeft size={13} /> Back to projects</button>
            {loadingPreview || !preview ? (
              <div className="py-12 text-center text-zinc-400"><CircleNotch size={24} className="animate-spin mx-auto mb-2" /><p className="text-sm">Analyzing {selected?.name}…</p></div>
            ) : (
              <>
                <div className="overflow-y-auto overflow-x-hidden pr-1 space-y-4 min-w-0" style={{ maxHeight: '56vh' }}>
                  <div className="rounded-lg border border-zinc-200 p-4">
                    <p className="text-sm font-semibold text-zinc-900">{selected?.name} <span className="text-zinc-400 font-normal">({selected?.key})</span></p>
                    <p className="text-2xl font-bold text-zinc-900 font-['Outfit'] mt-1" data-testid="jira-preview-count">{preview.count != null ? preview.count : preview.sampleCount}+ <span className="text-sm font-normal text-zinc-500">issues to import</span></p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-zinc-50 p-2.5"><Kanban size={16} className="mx-auto text-violet-500 mb-1" /><p className="text-[11px] text-zinc-500">Workflow statuses</p><p className="text-sm font-semibold">{preview.distinct_statuses?.length || 0}</p></div>
                    <div className="rounded-lg bg-zinc-50 p-2.5"><ListChecks size={16} className="mx-auto text-blue-500 mb-1" /><p className="text-[11px] text-zinc-500">Custom fields</p><p className="text-sm font-semibold">{preview.custom_fields?.length || 0}</p></div>
                    <div className="rounded-lg bg-zinc-50 p-2.5"><Tag size={16} className="mx-auto text-emerald-500 mb-1" /><p className="text-[11px] text-zinc-500">Story points</p><p className="text-sm font-semibold">{preview.has_story_points ? 'Yes' : '—'}</p></div>
                  </div>
                  {preview.sample?.length > 0 && (
                    <div className="rounded-lg border border-zinc-200 overflow-hidden">
                      <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold px-3 pt-2">Sample</p>
                      <div className="divide-y divide-zinc-100 max-h-[200px] overflow-y-auto">
                        {preview.sample.map(s => (
                          <div key={s.key} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                            <span className="text-zinc-400 w-16 shrink-0">{s.key}</span>
                            <span className="flex-1 min-w-0 truncate text-zinc-700">{s.title}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 shrink-0">{s.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-zinc-400">A new project <span className="font-medium text-zinc-600">"{selected?.name}"</span> will be created with these issues, columns, sprints, custom fields, <span className="font-medium text-zinc-600">comments and attachments</span>. Assignees are matched by email; unmatched ones stay unassigned. (First import covers up to 5,000 issues; attachments over 25&nbsp;MB are skipped.)</p>
                </div>
                <DialogFooter className="mt-1 pt-3 border-t border-zinc-100">
                  <Button variant="outline" onClick={() => setStep('projects')}>Back</Button>
                  <Button onClick={startImport} data-testid="jira-start-import"><DownloadSimple size={16} className="mr-1.5" /> Import to new project</Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}

        {/* STEP: Progress */}
        {step === 'progress' && (
          <div className="space-y-4 py-2" data-testid="jira-step-progress">
            {job?.status === 'error' ? (
              <div className="text-center py-6">
                <WarningCircle size={40} weight="fill" className="text-red-500 mx-auto mb-2" />
                <p className="font-semibold text-zinc-900">Import failed</p>
                <p className="text-sm text-zinc-500 mt-1">{job.error}</p>
              </div>
            ) : job?.status === 'done' ? (
              <div className="text-center py-2" data-testid="jira-import-done">
                <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto mb-2" />
                <p className="font-semibold text-zinc-900">Import complete</p>
                <div className="grid grid-cols-2 gap-2 text-left mt-4 text-sm">
                  <Stat label="Issues imported" value={job.created_count} />
                  <Stat label="Comments imported" value={job.comments_created} />
                  <Stat label="Attachments imported" value={job.attachments_created} />
                  <Stat label="Columns created" value={job.columns_created} />
                  <Stat label="Sprints created" value={job.sprints_created} />
                  <Stat label="Custom fields" value={job.custom_fields_created} />
                  {job.skipped_count > 0 && <Stat label="Skipped (dupes)" value={job.skipped_count} />}
                  {job.unassigned_count > 0 && <Stat label="Left unassigned" value={job.unassigned_count} />}
                </div>
                {job.unassigned_count > 0 && <p className="text-[11px] text-zinc-400 mt-2">{job.unassigned_count} issue(s) left unassigned — their Jira assignee isn't a member of this workspace (or Jira hid their email).</p>}
              </div>
            ) : (
              <div className="py-4">
                <p className="text-sm text-zinc-600 mb-3 flex items-center gap-2"><CircleNotch size={16} className="animate-spin text-blue-600" /> {fetched && !processed ? `Fetching issues from Jira (${fetched})…` : processed ? `Importing ${processed}${total ? ` / ${total}` : ''}…` : 'Starting…'}</p>
                <div className="h-2 rounded-full bg-zinc-100 overflow-hidden"><div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
              </div>
            )}
            <DialogFooter>
              {job?.status === 'done' && job.target_project_id ? (
                <>
                  <Button variant="outline" onClick={close}>Close</Button>
                  <Button onClick={() => { onOpenProject?.(job.target_project_id); close(); }} data-testid="jira-open-project"><ArrowRight size={16} className="mr-1.5" /> Open project</Button>
                </>
              ) : job?.status === 'error' ? (
                <Button variant="outline" onClick={() => setStep('projects')}>Try another project</Button>
              ) : (
                <Button variant="outline" onClick={close}>Run in background</Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-zinc-50 px-3 py-2">
      <p className="text-lg font-bold text-zinc-900">{value ?? 0}</p>
      <p className="text-[11px] text-zinc-500">{label}</p>
    </div>
  );
}
